import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { HexColorPicker } from "react-colorful";
import Select from "react-select";
import { getCategories, getProducts, updateProduct } from "../Redux/Reducers/ProductSlice";

const getExistingImageUrl = (image) =>
  image?.image_url || image?.url || image?.image || "";

const getExistingImageName = (image) => {
  const imageUrl = getExistingImageUrl(image);
  if (!imageUrl) return "";

  return decodeURIComponent(imageUrl.split("?")[0].split("/").pop() || "");
};

const EditPackageForm = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  
  const {
    categories = [],
    categoriesLoading = false,
    products = [],
    productsLoading = false,
    updating = false,
  } = useSelector((state) => state.products || {});

  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    category_id: "",
    type: "",
    buy_price: "",
    rent_price: "",
    buy_style: "",
    rent_style: "",
    color_name: "",
    color_code: "#000000",
    colors: [],
    featured_image: null,
    featured_image_color: null,
    gallery_images: [],
    gallery_image_colors: [],
    sizes: [],
    shoe_sizes: [],
    is_featured: false,
  });

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [featuredImagePreview, setFeaturedImagePreview] = useState(null);
  const [existingFeaturedImage, setExistingFeaturedImage] = useState(null);
  const [galleryImagePreviews, setGalleryImagePreviews] = useState([]);
  const [galleryImagePreviewColors, setGalleryImagePreviewColors] = useState([]);
  const [existingGalleryImages, setExistingGalleryImages] = useState([]);
  const [editedGalleryImages, setEditedGalleryImages] = useState({});
  const [editingGalleryIndex, setEditingGalleryIndex] = useState(null);
  const [pendingGalleryImages, setPendingGalleryImages] = useState([]);
  const [pendingGalleryImagePreviews, setPendingGalleryImagePreviews] =
    useState([]);
  const [pendingGalleryImageColor, setPendingGalleryImageColor] =
    useState(null);
  const featuredImageInputRef = useRef(null);
  const galleryImagesInputRef = useRef(null);
  const [sizeOptions, setSizeOptions] = useState([]);
  const [shoeSizeOptions, setShoeSizeOptions] = useState([]);
  const [originalFormData, setOriginalFormData] = useState({});

  const [selectedCoatTypes, setSelectedCoatTypes] = useState([]);
  const [selectedPantTypes, setSelectedPantTypes] = useState([]);
  const [coatMeasurementsMap, setCoatMeasurementsMap] = useState({});
  const [pantMeasurementsMap, setPantMeasurementsMap] = useState({});
  const [originalCoatTypes, setOriginalCoatTypes] = useState([]);
  const [originalPantTypes, setOriginalPantTypes] = useState([]);
  const [originalCoatMeasurements, setOriginalCoatMeasurements] = useState({});
  const [originalPantMeasurements, setOriginalPantMeasurements] = useState({});
  const [colorSizeSelections, setColorSizeSelections] = useState({});

  const typeOptions = [
    { value: "buy", label: "Buy Only" },
    { value: "rent", label: "Rent Only" },
    { value: "both", label: "Both (Buy & Rent)" },
  ];

  useEffect(() => {
    const loadProductData = async () => {
      await dispatch(getProducts());
      dispatch(getCategories());
    };

    loadProductData();
  }, [dispatch]);

  useEffect(() => {
    if (products.length > 0 && id) {
      const product = products.find(p => p.id === parseInt(id) || p.id === id);
      if (product) {
        let featuredImageUrl = null;
        let galleryUrls = [];
        let primaryImage = null;
        let galleryImages = [];
        
        if (product.images?.length > 0) {
          primaryImage = product.images.find(img => img.is_primary === true);
          if (primaryImage) featuredImageUrl = primaryImage.image_url;
          galleryImages = product.images.filter(img => img.is_primary !== true);
          galleryUrls = galleryImages.map(img => img.image_url);
        }
        
        let sizesArray = [];
        let shoeSizesArray = [];
        
        const colorMap = new Map();
        if (product.color_name) colorMap.set(product.color_name, product.color_code || "#000000");
        product.variants?.forEach((variant) => {
          if (variant.color) {
            colorMap.set(
              variant.color,
              variant.color_code || product.color_code || "#000000",
            );
          }
        });
        const productColors = Array.from(colorMap, ([name, code]) => ({
          name,
          code,
        }));
        const buyVariantWithStyle = product.variants?.find(
          (variant) => variant.buy_type === "buy" && variant.buy_style,
        );
        const rentVariantWithStyle = product.variants?.find(
          (variant) => variant.buy_type === "rent" && variant.rent_style,
        );
        const getImageColorOption = (image) => {
          const imageColorName = image?.color || image?.color_name || image?.name;
          const imageColorCode = image?.color_code || image?.colorCode;

          if (!imageColorName) return null;

          const matchedColor = productColors.find(
            (color) => color.name === imageColorName,
          );

          return {
            value: imageColorName,
            label: imageColorName,
            code: imageColorCode || matchedColor?.code || product.color_code || "#000000",
          };
        };

        const newFormData = {
          name: product.name || "",
          slug: product.slug || "",
          description: product.description || "",
          category_id: product.category_id || "",
          type: product.buy_type || "",
          buy_price: product.buy_price ? String(product.buy_price) : "",
          rent_price: product.rent_price ? String(product.rent_price) : "",
          buy_style: product.buy_style || buyVariantWithStyle?.buy_style || "", 
          rent_style: product.rent_style || rentVariantWithStyle?.rent_style || "",
          color_name: productColors.length ? "" : product.color_name || "",
          color_code: productColors.length ? "#000000" : product.color_code || "#000000",
          colors: productColors,
          featured_image: null,
          featured_image_color: getImageColorOption(primaryImage),
          gallery_images: [],
          gallery_image_colors: [],
          sizes: sizesArray,
          shoe_sizes: shoeSizesArray,
          is_featured: product.is_featured || false,
        };
        
        setFormData(newFormData);
        setOriginalFormData(newFormData);
        if (featuredImageUrl) setFeaturedImagePreview(featuredImageUrl);
        setExistingFeaturedImage(primaryImage || null);
        if (galleryUrls.length) setGalleryImagePreviews(galleryUrls);
        setExistingGalleryImages(galleryImages);
        setEditedGalleryImages({});
        setEditingGalleryIndex(null);
        setGalleryImagePreviewColors(galleryImages.map(getImageColorOption));

        if (product.category_id && categories.length > 0) {
          const category = categories.find(
            (c) => String(c.id) === String(product.category_id),
          );
          if (category) {
            setSelectedCategory(category);
            const nextColorSizeSelections = {};

            productColors.forEach((color) => {
              nextColorSizeSelections[`${color.name}-${color.code}`] = getEmptyColorSizeSelection();
            });

            product.variants?.forEach((variant) => {
              const colorName = variant.color || product.color_name;
              if (!colorName) return;

              const colorCode =
                variant.color_code ||
                productColors.find((color) => color.name === colorName)?.code ||
                product.color_code ||
                "#000000";
              const colorKey = `${colorName}-${colorCode}`;
              const current =
                nextColorSizeSelections[colorKey] || getEmptyColorSizeSelection();

              if (category.name === "Suits") {
                if (variant.size_category === "coat" && variant.size_type) {
                  if (!current.coatTypes.includes(variant.size_type)) {
                    current.coatTypes.push(variant.size_type);
                  }
                  if (!current.coatMeasurementsMap[variant.size_type]) {
                    current.coatMeasurementsMap[variant.size_type] = [];
                  }
                  if (
                    variant.size_measurement &&
                    !current.coatMeasurementsMap[variant.size_type].some(
                      (measurement) => measurement.value === variant.size_measurement,
                    )
                  ) {
                    current.coatMeasurementsMap[variant.size_type].push({
                      value: variant.size_measurement,
                      label: variant.size_measurement,
                    });
                  }
                } else if (variant.size_category === "pants" && variant.size_type) {
                  if (!current.pantTypes.includes(variant.size_type)) {
                    current.pantTypes.push(variant.size_type);
                  }
                  if (!current.pantMeasurementsMap[variant.size_type]) {
                    current.pantMeasurementsMap[variant.size_type] = [];
                  }
                  if (
                    variant.size_measurement &&
                    !current.pantMeasurementsMap[variant.size_type].some(
                      (measurement) => measurement.value === variant.size_measurement,
                    )
                  ) {
                    current.pantMeasurementsMap[variant.size_type].push({
                      value: variant.size_measurement,
                      label: variant.size_measurement,
                    });
                  }
                }
              } else if (category.name === "Shoes") {
                if (
                  variant.size_measurement &&
                  !current.shoe_sizes.some(
                    (size) => size.value === variant.size_measurement,
                  )
                ) {
                  current.shoe_sizes.push({
                    value: variant.size_measurement,
                    label: variant.size_measurement,
                  });
                }
              } else if (variant.size_type) {
                if (!current.sizes.some((size) => size.value === variant.size_type)) {
                  current.sizes.push({
                    value: variant.size_type,
                    label: variant.size_type,
                  });
                }
              }

              nextColorSizeSelections[colorKey] = current;
            });

            setColorSizeSelections(nextColorSizeSelections);
            
            if (category.name === "Suits" && product.variants) {
              const coatTypes = new Set();
              const pantTypes = new Set();
              const coatMeasurements = {};
              const pantMeasurements = {};
              
              product.variants.forEach(variant => {
                if (variant.size_category === "coat") {
                  coatTypes.add(variant.size_type);
                  if (!coatMeasurements[variant.size_type]) coatMeasurements[variant.size_type] = new Set();
                  if (variant.size_measurement) coatMeasurements[variant.size_type].add(variant.size_measurement);
                } else if (variant.size_category === "pants") {
                  pantTypes.add(variant.size_type);
                  if (!pantMeasurements[variant.size_type]) pantMeasurements[variant.size_type] = new Set();
                  if (variant.size_measurement) pantMeasurements[variant.size_type].add(variant.size_measurement);
                }
              });
              
              const coatTypesArray = Array.from(coatTypes);
              const pantTypesArray = Array.from(pantTypes);
              
              const coatMeasurementsArray = Object.fromEntries(
                Object.entries(coatMeasurements).map(([key, set]) => [key, Array.from(set).map(m => ({ value: m, label: m }))])
              );
              
              const pantMeasurementsArray = Object.fromEntries(
                Object.entries(pantMeasurements).map(([key, set]) => [key, Array.from(set).map(m => ({ value: m, label: m }))])
              );
              
              setSelectedCoatTypes(coatTypesArray);
              setSelectedPantTypes(pantTypesArray);
              setCoatMeasurementsMap(coatMeasurementsArray);
              setPantMeasurementsMap(pantMeasurementsArray);
              setOriginalCoatTypes(coatTypesArray);
              setOriginalPantTypes(pantTypesArray);
              setOriginalCoatMeasurements(coatMeasurementsArray);
              setOriginalPantMeasurements(pantMeasurementsArray);
            } else if (["Shirts", "Ties", "Belts"].includes(category.name)) {
              const sizes = category?.size_options?.data?.size_types || [];
              const extractedSizes = product.variants?.length 
                ? [...new Set(product.variants.map(v => v.size_type).filter(Boolean))].map(s => ({ value: s, label: s }))
                : [];
              setSizeOptions(sizes.map(s => ({ value: s, label: s })));
              if (extractedSizes.length) {
                setFormData(prev => ({ ...prev, sizes: extractedSizes }));
                setOriginalFormData(prev => ({ ...prev, sizes: extractedSizes }));
              }
            } else if (["Bow Ties", "Pocket Square", "Socks"].includes(category.name)) {
              setSizeOptions([{ value: "Standard", label: "Standard" }]);
              setFormData(prev => ({ ...prev, sizes: [{ value: "Standard", label: "Standard" }] }));
              setOriginalFormData(prev => ({ ...prev, sizes: [{ value: "Standard", label: "Standard" }] }));
            } else if (category.name === "Shoes") {
              const measurements = category?.size_options?.data?.measurements || [];
              const extractedMeasurements = product.variants?.length 
                ? [...new Set(product.variants.map(v => v.size_measurement).filter(Boolean))].map(m => ({ value: m, label: m }))
                : [];
              setShoeSizeOptions(measurements.map(m => ({ value: m, label: m })));
              if (extractedMeasurements.length) {
                setFormData(prev => ({ ...prev, shoe_sizes: extractedMeasurements }));
                setOriginalFormData(prev => ({ ...prev, shoe_sizes: extractedMeasurements }));
              }
            }
          }
        }
      }
    }
  }, [products, id, categories]);

  useEffect(() => {
    if (formData.name) {
      const slug = formData.name.toLowerCase().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-").replace(/--+/g, "-").trim();
      setFormData(prev => ({ ...prev, slug }));
    }
  }, [formData.name]);

  const handleCategoryChange = (selectedOption) => {
    const category = categories.find(
      (c) => String(c.id) === String(selectedOption.value),
    );
    setSelectedCategory(category);

    const newFormData = {
      ...formData,
      category_id: selectedOption.value,
      type: "",
      buy_price: "",
      rent_price: "",
      buy_style: "",
      rent_style: "",
      sizes: [],
      shoe_sizes: [],
    };
    
    setFormData(newFormData);
    setOriginalFormData(newFormData);
    setSelectedCoatTypes([]);
    setSelectedPantTypes([]);
    setCoatMeasurementsMap({});
    setPantMeasurementsMap({});
    setColorSizeSelections({});
    setSizeOptions([]);
    setShoeSizeOptions([]);

    if (["Shirts", "Ties", "Belts"].includes(category.name)) {
      const sizes = category?.size_options?.data?.size_types || [];
      setSizeOptions(sizes.map(s => ({ value: s, label: s })));
    } else if (["Bow Ties", "Pocket Square", "Socks"].includes(category.name)) {
      setSizeOptions([{ value: "Standard", label: "Standard" }]);
      setFormData(prev => ({ ...prev, sizes: [{ value: "Standard", label: "Standard" }] }));
    } else if (category.name === "Shoes") {
      const measurements = category?.size_options?.data?.measurements || [];
      setShoeSizeOptions(measurements.map(m => ({ value: m, label: m })));
    }
  };

  const handleFeaturedImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, featured_image: file }));
      setExistingFeaturedImage(null);
      const reader = new FileReader();
      reader.onloadend = () => setFeaturedImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleGalleryImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length) {
      const nextFiles =
        editingGalleryIndex !== null ? files.slice(0, 1) : files;
      setPendingGalleryImages(nextFiles);
      const newPreviews = [];
      nextFiles.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          newPreviews.push(reader.result);
          if (newPreviews.length === nextFiles.length) {
            setPendingGalleryImagePreviews(newPreviews);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const getImageColorValue = (colorName) => {
    const trimmedColorName = colorName.trim();
    const matchedColor = getSelectedColors().find(
      (color) => color.name.toLowerCase() === trimmedColorName.toLowerCase(),
    );

    if (!trimmedColorName) return null;

    return {
      value: matchedColor?.name || trimmedColorName,
      label: matchedColor?.name || trimmedColorName,
      code: matchedColor?.code || "",
    };
  };

  const handleFeaturedImageColorChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      featured_image_color: getImageColorValue(e.target.value),
    }));
  };

  const handlePendingGalleryImageColorChange = (e) => {
    setPendingGalleryImageColor(getImageColorValue(e.target.value));
  };

  const resetGalleryEditor = () => {
    setPendingGalleryImages([]);
    setPendingGalleryImagePreviews([]);
    setPendingGalleryImageColor(null);
    setEditingGalleryIndex(null);
    if (galleryImagesInputRef.current) {
      galleryImagesInputRef.current.value = "";
    }
  };

  const handleEditGalleryImage = (index) => {
    setEditingGalleryIndex(index);
    setPendingGalleryImages([]);
    setPendingGalleryImagePreviews([]);
    setPendingGalleryImageColor(galleryImagePreviewColors[index] || null);
    if (galleryImagesInputRef.current) {
      galleryImagesInputRef.current.value = "";
    }
  };

  const handleAddGalleryImages = () => {
    if (editingGalleryIndex !== null) {
      if (selectedCategory?.name !== "Suits" && !pendingGalleryImageColor) {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Please enter gallery image color!",
        });
        return;
      }

      setGalleryImagePreviewColors((prev) =>
        prev.map((color, index) =>
          index === editingGalleryIndex
            ? selectedCategory?.name === "Suits"
              ? null
              : pendingGalleryImageColor
            : color,
        ),
      );

      if (pendingGalleryImagePreviews[0]) {
        setGalleryImagePreviews((prev) =>
          prev.map((preview, index) =>
            index === editingGalleryIndex
              ? pendingGalleryImagePreviews[0]
              : preview,
          ),
        );
      }

      const existingPreviewCount =
        galleryImagePreviews.length - formData.gallery_images.length;
      const newImageIndex = editingGalleryIndex - existingPreviewCount;

      if (newImageIndex >= 0) {
        setFormData((prev) => ({
          ...prev,
          gallery_images: prev.gallery_images.map((image, index) =>
            index === newImageIndex && pendingGalleryImages[0]
              ? pendingGalleryImages[0]
              : image,
          ),
          gallery_image_colors: prev.gallery_image_colors.map((color, index) =>
            index === newImageIndex
              ? selectedCategory?.name === "Suits"
                ? null
                : pendingGalleryImageColor
              : color,
          ),
        }));
      } else {
        setEditedGalleryImages((prev) => ({
          ...prev,
          [editingGalleryIndex]: {
            image: pendingGalleryImages[0] || null,
            color: selectedCategory?.name === "Suits" ? null : pendingGalleryImageColor,
          },
        }));
      }

      resetGalleryEditor();
      return;
    }

    if (pendingGalleryImages.length === 0) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Please select gallery image!",
      });
      return;
    }

    if (selectedCategory?.name !== "Suits" && !pendingGalleryImageColor) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Please enter gallery image color!",
      });
      return;
    }

    setFormData((prev) => ({
      ...prev,
      gallery_images: [...prev.gallery_images, ...pendingGalleryImages],
      gallery_image_colors: [
        ...prev.gallery_image_colors,
        ...pendingGalleryImages.map(() =>
          selectedCategory?.name === "Suits" ? null : pendingGalleryImageColor,
        ),
      ],
    }));
    setGalleryImagePreviews((prev) => [
      ...prev,
      ...pendingGalleryImagePreviews,
    ]);
    setGalleryImagePreviewColors((prev) => [
      ...prev,
      ...pendingGalleryImages.map(() =>
        selectedCategory?.name === "Suits" ? null : pendingGalleryImageColor,
      ),
    ]);
    resetGalleryEditor();
  };

  const removeGalleryImage = (index) => {
    const existingPreviewCount =
      galleryImagePreviews.length - formData.gallery_images.length;
    const newImageIndex = index - existingPreviewCount;

    setFormData(prev => ({
      ...prev,
      gallery_images:
        newImageIndex >= 0
          ? prev.gallery_images.filter((_, i) => i !== newImageIndex)
          : prev.gallery_images,
      gallery_image_colors:
        newImageIndex >= 0
          ? prev.gallery_image_colors.filter((_, i) => i !== newImageIndex)
          : prev.gallery_image_colors,
    }));
    setGalleryImagePreviews(prev => prev.filter((_, i) => i !== index));
    setGalleryImagePreviewColors(prev => prev.filter((_, i) => i !== index));
    setExistingGalleryImages(prev => prev.filter((_, i) => i !== index));
    setEditedGalleryImages((prev) => {
      const updated = {};
      Object.entries(prev).forEach(([key, value]) => {
        const currentIndex = Number(key);
        if (currentIndex < index) updated[currentIndex] = value;
        if (currentIndex > index) updated[currentIndex - 1] = value;
      });
      return updated;
    });
    if (editingGalleryIndex === index) {
      resetGalleryEditor();
    } else if (editingGalleryIndex > index) {
      setEditingGalleryIndex((prev) => prev - 1);
    }
  };

  const removeFeaturedImage = () => {
    setFormData(prev => ({ ...prev, featured_image: null, featured_image_color: null }));
    setExistingFeaturedImage(null);
    setFeaturedImagePreview(null);
  };

  const handleTypeChange = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      type: selectedOption.value,
      buy_price: "",
      rent_price: "",
      buy_style: selectedOption.value === "rent" ? "" : prev.buy_style,
      rent_style: selectedOption.value === "buy" ? "" : prev.rent_style,
    }));
  };

  const handleBuyStyleChange = (e) => {
    setFormData(prev => ({ ...prev, buy_style: e.target.value }));
  };

  const handleRentStyleChange = (e) => {
    setFormData(prev => ({ ...prev, rent_style: e.target.value }));
  };

  const handleBuyPriceChange = (e) => setFormData(prev => ({ ...prev, buy_price: e.target.value }));
  const handleRentPriceChange = (e) => setFormData(prev => ({ ...prev, rent_price: e.target.value }));
  const handleColorChange = (color) => setFormData(prev => ({ ...prev, color_code: color }));
  const handleColorNameChange = (e) => setFormData(prev => ({ ...prev, color_name: e.target.value }));
  const getSelectedColors = () => {
    if (formData.colors.length > 0) return formData.colors;
    if (formData.color_name.trim()) {
      return [{ name: formData.color_name.trim(), code: formData.color_code }];
    }
    return [];
  };
  const getColorOptions = () =>
    getSelectedColors().map((color) => ({
      value: color.name,
      label: color.name,
      code: color.code,
    }));
  const handleAddColor = () => {
    const colorName = formData.color_name.trim();

    if (!colorName) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Please enter color name!",
      });
      return;
    }

    const alreadyAdded = formData.colors.some(
      (color) => color.name.toLowerCase() === colorName.toLowerCase(),
    );

    if (alreadyAdded) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "This color is already added!",
      });
      return;
    }

    setFormData((prev) => ({
      ...prev,
      colors: [...prev.colors, { name: colorName, code: prev.color_code }],
      color_name: "",
      color_code: "#000000",
    }));
    setShowColorPicker(false);
  };
  const handleRemoveColor = (index) => {
    const colorToRemove = formData.colors[index];
    setFormData((prev) => ({
      ...prev,
      colors: prev.colors.filter((_, colorIndex) => colorIndex !== index),
    }));
    if (colorToRemove) {
      setColorSizeSelections((prev) => {
        const updatedSelections = { ...prev };
        delete updatedSelections[getColorKey(colorToRemove)];
        return updatedSelections;
      });
    }
  };
  const getColorKey = (color) => `${color.name}-${color.code}`;
  const getEmptyColorSizeSelection = () => ({
    coatTypes: [],
    pantTypes: [],
    coatMeasurementsMap: {},
    pantMeasurementsMap: {},
    sizes: [],
    shoe_sizes: [],
  });
  const getColorSizeSelection = (color) =>
    colorSizeSelections[getColorKey(color)] || getEmptyColorSizeSelection();
  const updateColorSizeSelection = (color, updater) => {
    const colorKey = getColorKey(color);
    setColorSizeSelections((prev) => {
      const currentSelection = prev[colorKey] || getEmptyColorSizeSelection();
      const nextSelection =
        typeof updater === "function"
          ? updater(currentSelection)
          : { ...currentSelection, ...updater };

      return {
        ...prev,
        [colorKey]: nextSelection,
      };
    });
  };
  const stripHtmlTags = (html) => {
    const temp = document.createElement("div");
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || "";
  };

  const getErrorMessage = (error) => {
    if (!error) return "Something went wrong while updating the product!";
    if (typeof error === "string") return error;
    if (error.message) return error.message;
    if (error.response?.data?.message) return error.response.data.message;
    if (error.response?.data?.errors) {
      const errors = error.response.data.errors;
      if (typeof errors === "string") return errors;
      return Object.values(errors)
        .flat()
        .map((msg) => (typeof msg === "string" ? msg : JSON.stringify(msg)))
        .join("\n");
    }
    if (error.errors) {
      const errors = error.errors;
      if (typeof errors === "string") return errors;
      return Object.values(errors)
        .flat()
        .map((msg) => (typeof msg === "string" ? msg : JSON.stringify(msg)))
        .join("\n");
    }
    return "Something went wrong while updating the product!";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (getSelectedColors().length === 0) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Please add at least one color!",
      });
      return;
    }

    if (!formData.featured_image && !existingFeaturedImage) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Please upload featured image!",
      });
      return;
    }

    if (selectedCategory?.name !== "Suits" && !formData.featured_image_color) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Please select featured image color!",
      });
      return;
    }

    const submitData = new FormData();
    submitData.append("name", formData.name);
    submitData.append("slug", formData.slug);
    submitData.append("description", stripHtmlTags(formData.description));
    submitData.append("category_id", formData.category_id);
    submitData.append("buy_type", formData.type);
    const selectedColors = getSelectedColors();
    const primaryColor = selectedColors[0];

    submitData.append("color_name", primaryColor?.name || "");
    submitData.append("color_code", primaryColor?.code || "");
    selectedColors.forEach((color, index) => {
      submitData.append(`colors[${index}][name]`, color.name);
      submitData.append(`colors[${index}][code]`, color.code);
    });

    submitData.append("is_featured", formData.is_featured ? "1" : "0");

    // Only send price/style fields that apply to the selected product type.
    if (formData.type === "buy" || formData.type === "both") {
      submitData.append("buy_price", formData.buy_price || "");
      submitData.append("buy_style", formData.buy_style || "");
    }

    if (formData.type === "rent" || formData.type === "both") {
      submitData.append("rent_price", formData.rent_price || "");
      submitData.append("rent_style", formData.rent_style || "");
    }
    
    if (formData.featured_image || existingFeaturedImage) {
      const featuredImage = formData.featured_image || getExistingImageName(existingFeaturedImage);

      if (existingFeaturedImage?.id) {
        submitData.append("images[0][id]", existingFeaturedImage.id);
      }
      if (featuredImage) {
        submitData.append("images[0][image]", featuredImage);
      }
      submitData.append("images[0][is_primary]", "1");
      if (selectedCategory?.name !== "Suits" && formData.featured_image_color) {
        submitData.append("images[0][color]", formData.featured_image_color.label);
      }
    }

    let imageIndex = 1;
    existingGalleryImages.forEach((existingImage, galleryIndex) => {
      const editedImage = editedGalleryImages[galleryIndex];
      const image = editedImage?.image || getExistingImageName(existingImage);
      const imageColor = editedImage?.color || galleryImagePreviewColors[galleryIndex];

      if (existingImage.id) {
        submitData.append(`images[${imageIndex}][id]`, existingImage.id);
      }
      if (image) {
        submitData.append(`images[${imageIndex}][image]`, image);
      }
      submitData.append(`images[${imageIndex}][is_primary]`, "0");
      if (selectedCategory?.name !== "Suits" && imageColor) {
        submitData.append(`images[${imageIndex}][color]`, imageColor.label);
      }
      imageIndex += 1;
    });

    formData.gallery_images.forEach((image, index) => {
      const imageColor = formData.gallery_image_colors[index];

      submitData.append(`images[${imageIndex}][image]`, image);
      submitData.append(`images[${imageIndex}][is_primary]`, "0");
      if (selectedCategory?.name !== "Suits" && imageColor) {
        submitData.append(`images[${imageIndex}][color]`, imageColor.label);
      }
      imageIndex += 1;
    });

    let variantsArray = [];

    if (selectedCategory?.name === "Suits") {
      selectedColors.forEach((color) => {
        const selection = getColorSizeSelection(color);

        selection.coatTypes.forEach((coatType) => {
          const measurements = selection.coatMeasurementsMap[coatType] || [];
          measurements.forEach((measurement) => {
            if (formData.type === "both") {
              variantsArray.push({
                size_category: "coat",
                size_type: coatType,
                size_measurement: measurement.value,
                color: color.name,
                color_code: color.code,
                buy_type: "buy",
                buy_style: formData.buy_style,
                stock_quantity: 1,
                price: formData.buy_price,
              });
              variantsArray.push({
                size_category: "coat",
                size_type: coatType,
                size_measurement: measurement.value,
                color: color.name,
                color_code: color.code,
                buy_type: "rent",
                rent_style: formData.rent_style,
                stock_quantity: 1,
                price: formData.rent_price,
              });
            } else {
              variantsArray.push({
                size_category: "coat",
                size_type: coatType,
                size_measurement: measurement.value,
                color: color.name,
                color_code: color.code,
                buy_type: formData.type,
                ...(formData.type === "buy"
                  ? { buy_style: formData.buy_style }
                  : { rent_style: formData.rent_style }),
                stock_quantity: 1,
                price: formData.type === "buy" ? formData.buy_price : formData.rent_price,
              });
            }
          });
        });

        selection.pantTypes.forEach((pantType) => {
          const measurements = selection.pantMeasurementsMap[pantType] || [];
          measurements.forEach((measurement) => {
            if (formData.type === "both") {
              variantsArray.push({
                size_category: "pants",
                size_type: pantType,
                size_measurement: measurement.value,
                color: color.name,
                color_code: color.code,
                buy_type: "buy",
                buy_style: formData.buy_style,
                stock_quantity: 1,
                price: formData.buy_price,
              });
              variantsArray.push({
                size_category: "pants",
                size_type: pantType,
                size_measurement: measurement.value,
                color: color.name,
                color_code: color.code,
                buy_type: "rent",
                rent_style: formData.rent_style,
                stock_quantity: 1,
                price: formData.rent_price,
              });
            } else {
              variantsArray.push({
                size_category: "pants",
                size_type: pantType,
                size_measurement: measurement.value,
                color: color.name,
                color_code: color.code,
                buy_type: formData.type,
                ...(formData.type === "buy"
                  ? { buy_style: formData.buy_style }
                  : { rent_style: formData.rent_style }),
                stock_quantity: 1,
                price: formData.type === "buy" ? formData.buy_price : formData.rent_price,
              });
            }
          });
        });
      });
    } else if (
      [
        "Shirts",
        "Ties",
        "Belts",
        "Bow Ties",
        "Pocket Square",
        "Socks",
      ].includes(selectedCategory?.name)
    ) {
      selectedColors.forEach((color) => {
        const selection = getColorSizeSelection(color);
        const colorSizes = ["Bow Ties", "Pocket Square", "Socks"].includes(
          selectedCategory?.name,
        )
          ? [{ value: "Standard", label: "Standard" }]
          : selection.sizes;

        if (colorSizes && colorSizes.length > 0) {
          colorSizes.forEach((size) => {
          if (formData.type === "both") {
            // Buy variant
            variantsArray.push({
              size_type: size.value,
              color: color.name,
              color_code: color.code,
              buy_type: "buy",
              buy_style: formData.buy_style,
              stock_quantity: 1,
              price: formData.buy_price,
            });
            // Rent variant
            variantsArray.push({
              size_type: size.value,
              color: color.name,
              color_code: color.code,
              buy_type: "rent",
              rent_style: formData.rent_style,
              stock_quantity: 1,
              price: formData.rent_price,
            });
          } else {
            variantsArray.push({
              size_type: size.value,
              color: color.name,
              color_code: color.code,
              buy_type: formData.type,
              ...(formData.type === "buy"
                ? { buy_style: formData.buy_style }
                : { rent_style: formData.rent_style }),
              stock_quantity: 1,
              price: formData.type === "buy" ? formData.buy_price : formData.rent_price,
            });
          }
        });
        }
      });
    } else if (selectedCategory?.name === "Shoes") {
      selectedColors.forEach((color) => {
        const selection = getColorSizeSelection(color);

        if (selection.shoe_sizes && selection.shoe_sizes.length > 0) {
          selection.shoe_sizes.forEach((size) => {
          if (formData.type === "both") {
            // Buy variant
            variantsArray.push({
              size_measurement: size.value,
              color: color.name,
              color_code: color.code,
              buy_type: "buy",
              buy_style: formData.buy_style,
              stock_quantity: 1,
              price: formData.buy_price,
            });
            // Rent variant
            variantsArray.push({
              size_measurement: size.value,
              color: color.name,
              color_code: color.code,
              buy_type: "rent",
              rent_style: formData.rent_style,
              stock_quantity: 1,
              price: formData.rent_price,
            });
          } else {
            variantsArray.push({
              size_measurement: size.value,
              color: color.name,
              color_code: color.code,
              buy_type: formData.type,
              ...(formData.type === "buy"
                ? { buy_style: formData.buy_style }
                : { rent_style: formData.rent_style }),
              stock_quantity: 1,
              price: formData.type === "buy" ? formData.buy_price : formData.rent_price,
            });
          }
        });
        }
      });
    }

    if (variantsArray.length > 0) {
      variantsArray.forEach((variant, index) => {
        Object.entries(variant).forEach(([key, value]) => {
          submitData.append(`variants[${index}][${key}]`, value);
        });
      });
    } else {
      submitData.append("variants[]", "");
    }

    try {
      await dispatch(updateProduct({ productId: id, productData: submitData })).unwrap();
      await dispatch(getProducts());
      Swal.fire({ icon: "success", title: "Product Updated Successfully!", showConfirmButton: false, timer: 2000 });
      setOriginalFormData(formData);
      setOriginalCoatTypes(selectedCoatTypes);
      setOriginalPantTypes(selectedPantTypes);
      setOriginalCoatMeasurements(coatMeasurementsMap);
      setOriginalPantMeasurements(pantMeasurementsMap);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: getErrorMessage(error),
      });
    }
  };

  const categoryOptions = categories.map(category => ({ value: category.id, label: category.name }));

  const renderColorSizeFields = () => {
    const selectedColors = getSelectedColors();

    if (!selectedCategory || selectedColors.length === 0) return null;

    return (
      <div className="mb-4">
        {selectedColors.map((color) => {
          const selection = getColorSizeSelection(color);
          const colorKey = getColorKey(color);

          return (
            <div key={colorKey} className="border rounded p-3 mb-3">
              <div className="d-flex align-items-center gap-2 mb-3">
                <span
                  style={{
                    width: "20px",
                    height: "20px",
                    backgroundColor: color.code,
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    display: "inline-block",
                  }}
                />
                <h6 className="mb-0">{color.name} Sizes</h6>
                {/* <small className="text-muted">{color.code}</small> */}
              </div>

              {selectedCategory.name === "Suits" && (
                <>
                  <label className="form-label fw-bold mb-2">
                    Coat Size & Measurement <span>*</span>
                  </label>
                  <div className="size-tabs">
                    {["short", "regular", "long", "xl"].map((type) => (
                      <button
                        key={`${colorKey}-coat-${type}`}
                        type="button"
                        className={`btn ${selection.coatTypes.includes(type) ? "active" : "btn-outline-secondary"}`}
                        onClick={() => {
                          updateColorSizeSelection(color, (current) => {
                            if (current.coatTypes.includes(type)) {
                              const newMap = { ...current.coatMeasurementsMap };
                              delete newMap[type];
                              return {
                                ...current,
                                coatTypes: current.coatTypes.filter((t) => t !== type),
                                coatMeasurementsMap: newMap,
                              };
                            }

                            return {
                              ...current,
                              coatTypes: [...current.coatTypes, type],
                            };
                          });
                        }}
                        style={{ minWidth: "80px" }}
                      >
                        {type.toUpperCase()}
                      </button>
                    ))}
                  </div>

                  {selection.coatTypes.map((type) => {
                    const coatData = selectedCategory?.size_options?.data?.categories?.find(cat => cat.name === "coat");
                    const sizeType = coatData?.types?.find(t => t.name === type);
                    const measurementsList = sizeType?.measurements?.map(m => ({ value: m, label: m })) || [];

                    return (
                      <div key={`${colorKey}-coat-measurement-${type}`} className="mt-3">
                        <label className="form-label fw-bold">
                          {color.name} {type.toUpperCase()} Coat Measurements <span>*</span>
                        </label>
                        <Select
                          name={`${colorKey}_coatMeasurements_${type}`}
                          isMulti
                          required
                          options={measurementsList}
                          value={selection.coatMeasurementsMap[type] || []}
                          onChange={(selectedOptions) => {
                            updateColorSizeSelection(color, (current) => ({
                              ...current,
                              coatMeasurementsMap: {
                                ...current.coatMeasurementsMap,
                                [type]: selectedOptions || [],
                              },
                            }));
                          }}
                          placeholder={`Select ${color.name} ${type.toUpperCase()} coat measurements`}
                          isSearchable
                          className="select"
                        />
                      </div>
                    );
                  })}

                  <label className="form-label fw-bold mt-4 mb-2">
                    Pant Size & Measurement <span>*</span>
                  </label>
                  <div className="size-tabs">
                    {["short", "regular", "long", "xl"].map((type) => (
                      <button
                        key={`${colorKey}-pant-${type}`}
                        type="button"
                        className={`btn ${selection.pantTypes.includes(type) ? "active" : "btn-outline-secondary"}`}
                        onClick={() => {
                          updateColorSizeSelection(color, (current) => {
                            if (current.pantTypes.includes(type)) {
                              const newMap = { ...current.pantMeasurementsMap };
                              delete newMap[type];
                              return {
                                ...current,
                                pantTypes: current.pantTypes.filter((t) => t !== type),
                                pantMeasurementsMap: newMap,
                              };
                            }

                            return {
                              ...current,
                              pantTypes: [...current.pantTypes, type],
                            };
                          });
                        }}
                        style={{ minWidth: "80px" }}
                      >
                        {type.toUpperCase()}
                      </button>
                    ))}
                  </div>

                  {selection.pantTypes.map((type) => {
                    const pantData = selectedCategory?.size_options?.data?.categories?.find(cat => cat.name === "pants");
                    const sizeType = pantData?.types?.find(t => t.name === type);
                    const measurementsList = sizeType?.measurements?.map(m => ({ value: m, label: m })) || [];

                    return (
                      <div key={`${colorKey}-pant-measurement-${type}`} className="mt-3">
                        <label className="form-label fw-bold">
                          {color.name} {type.toUpperCase()} Pant Measurements <span>*</span>
                        </label>
                        <Select
                          name={`${colorKey}_pantMeasurements_${type}`}
                          isMulti
                          required
                          options={measurementsList}
                          value={selection.pantMeasurementsMap[type] || []}
                          onChange={(selectedOptions) => {
                            updateColorSizeSelection(color, (current) => ({
                              ...current,
                              pantMeasurementsMap: {
                                ...current.pantMeasurementsMap,
                                [type]: selectedOptions || [],
                              },
                            }));
                          }}
                          placeholder={`Select ${color.name} ${type.toUpperCase()} pant measurements`}
                          isSearchable
                          className="select"
                        />
                      </div>
                    );
                  })}
                </>
              )}

              {["Shirts", "Ties", "Belts"].includes(selectedCategory.name) && (
                <div>
                  <label className="form-label">
                    Select {color.name} Sizes <span>*</span>
                  </label>
                  <Select
                    name={`${colorKey}_sizes`}
                    isMulti
                    required
                    options={sizeOptions}
                    value={selection.sizes}
                    onChange={(selectedOptions) => {
                      updateColorSizeSelection(color, {
                        sizes: selectedOptions || [],
                      });
                    }}
                    placeholder={`Select ${color.name} sizes`}
                    isSearchable
                    className="select"
                  />
                </div>
              )}

              {["Bow Ties", "Pocket Square", "Socks"].includes(selectedCategory.name) && (
                <div>
                  <label className="form-label">
                    {color.name} Size <span>*</span>
                  </label>
                  <input
                    type="text"
                    value="Standard"
                    disabled
                    className="form-control"
                  />
                </div>
              )}

              {selectedCategory.name === "Shoes" && (
                <div>
                  <label className="form-label">
                    Select {color.name} Shoe Sizes <span>*</span>
                  </label>
                  <Select
                    name={`${colorKey}_shoeSizes`}
                    isMulti
                    required
                    options={shoeSizeOptions}
                    value={selection.shoe_sizes}
                    onChange={(selectedOptions) => {
                      updateColorSizeSelection(color, {
                        shoe_sizes: selectedOptions || [],
                      });
                    }}
                    placeholder={`Select ${color.name} shoe sizes`}
                    isSearchable
                    className="select"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  if (productsLoading || categoriesLoading) {
    return (
      <div className="container mt-4 text-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={`container mt-4 mainForm${selectedCategory?.name === "Suits" ? " suit" : ""}`}
      encType="multipart/form-data"
    >
      <div className="mb-3">
        <label htmlFor="name" className="form-label">
          Product Name <span>*</span>
        </label>
        <input
          type="text"
          className="form-control"
          id="name"
          name="name"
          value={formData.name}
          required
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          placeholder="Product Name"
        />
      </div>
      {/* <div className="mb-3">
        <label htmlFor="slug" className="form-label">
          Slug
        </label>
        <input
          type="text"
          className="form-control"
          id="slug"
          name="slug"
          value={formData.slug}
          readOnly
          disabled
          placeholder="Slug"
        />
      </div> */}
      <div className="mb-3">
        <label htmlFor="description" className="form-label">
          Description <span>*</span>
        </label>
        <ReactQuill
          theme="snow"
          value={formData.description}
          onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
          id="description"
          name="description"
        />
      </div>

      <div className="row align-items-end">
        <div className="mb-3 col-md-5">
          <label htmlFor="color_name" className="form-label">
            Color Name <span>*</span>
          </label>
          <input
            type="text"
            className="form-control"
            id="color_name"
            name="color_name"
            value={formData.color_name}
            required={formData.colors.length === 0}
            onChange={handleColorNameChange}
            placeholder="Color Name"
          />
        </div>
        <div className="mb-3 col-md-5">
          <label htmlFor="color_code" className="form-label">
            Color Code <span>*</span>
          </label>
          <div className="position-relative">
            <div
              className="d-flex align-items-center gap-2 rounded color-field"
              style={{ cursor: "pointer" }}
              onClick={() => setShowColorPicker(!showColorPicker)}
            >
              <div
                style={{
                  width: "30px",
                  height: "30px",
                  backgroundColor: formData.color_code,
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
              />
              <input
                type="text"
                className="form-control border-0 p-0"
                id="color_code"
                name="color_code"
                value={formData.color_code}
                required={formData.colors.length === 0}
                onChange={(e) => handleColorChange(e.target.value)}
                placeholder="#000000"
                style={{ boxShadow: "none", outline: "none" }}
              />
            </div>
            {showColorPicker && (
              <div className="position-absolute mt-2" style={{ zIndex: 1000 }}>
                <HexColorPicker color={formData.color_code} onChange={handleColorChange} />
              </div>
            )}
          </div>
        </div>
        <div className="mb-3 col-md-2 color-button">
          <button
            type="button"
            className="designBtn"
            onClick={handleAddColor}
          >
            Add
          </button>
        </div>
      </div>
      {formData.colors.length > 0 && (
        <div className="mb-3 d-flex flex-wrap gap-2">
          {formData.colors.map((color, index) => (
            <div
              key={`${color.name}-${color.code}`}
              className="d-flex align-items-center color-box"
            >
              <span
                style={{
                  width: "18px",
                  height: "18px",
                  backgroundColor: color.code,
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  display: "inline-block",
                }}
              />
              <span>{color.name}</span>
              {/* <small className="text-muted">{color.code}</small> */}
              <button
                type="button"
                className="designBtn"
                onClick={() => handleRemoveColor(index)}
                aria-label={`Remove ${color.name}`}
              >
                x
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mb-3">
        <label htmlFor="category" className="form-label">
          Category <span>*</span>
        </label>
        <Select
          name="category"
          id="category"
          required
          options={categoryOptions}
          value={categoryOptions.find(opt => opt.value === formData.category_id)}
          onChange={handleCategoryChange}
          placeholder="Select a category"
          isSearchable
          isLoading={categoriesLoading}
          className="select"
        />
      </div>
      
      {renderColorSizeFields()}

      <div className="mb-3">
        <label htmlFor="type" className="form-label">
          Product Type <span>*</span>
        </label>
        <Select
          name="type"
          id="type"
          required
          options={typeOptions}
          value={typeOptions.find(opt => opt.value === formData.type) || null}
          onChange={handleTypeChange}
          placeholder="Select product type"
          isSearchable
          className="select"
        />
      </div>
      {(formData.type === "buy" || formData.type === "both") && (
        <div className="mb-3">
          <label htmlFor="buy_style" className="form-label">
            Buy Style <span>*</span>
          </label>
          <input
            type="text"
            className="form-control"
            id="buy_style"
            name="buy_style"
            value={formData.buy_style}
            required={formData.type === "buy" || formData.type === "both"}
            onChange={handleBuyStyleChange}
            placeholder="Enter buy style (e.g., Classic, Modern)"
          />
        </div>
      )}
      {(formData.type === "rent" || formData.type === "both") && (
        <div className="mb-3">
          <label htmlFor="rent_style" className="form-label">
            Rent Style <span>*</span>
          </label>
          <input
            type="text"
            className="form-control"
            id="rent_style"
            name="rent_style"
            value={formData.rent_style}
            required={formData.type === "rent" || formData.type === "both"}
            onChange={handleRentStyleChange}
            placeholder="Enter rent style (e.g., Classic, Modern)"
          />
        </div>
      )}

      {(formData.type === "buy" || formData.type === "both") && (
        <div className="mb-3">
          <label htmlFor="buy_price" className="form-label">
            Buy Price ($) <span>*</span>
          </label>
          <input
            type="text"
            className="form-control"
            id="buy_price"
            name="buy_price"
            value={formData.buy_price}
            required={formData.type === "buy" || formData.type === "both"}
            onChange={handleBuyPriceChange}
            placeholder="Enter buy price"
          />
        </div>
      )}

      {(formData.type === "rent" || formData.type === "both") && (
        <div className="mb-3">
          <label htmlFor="rent_price" className="form-label">
            Rent Price ($) <span>*</span>
          </label>
          <input
            type="text"
            className="form-control"
            id="rent_price"
            name="rent_price"
            value={formData.rent_price}
            required={formData.type === "rent" || formData.type === "both"}
            onChange={handleRentPriceChange}
            placeholder="Enter rent price"
          />
        </div>
      )}

      <div className="mb-3">
        <div className="row">
          <div className="col-md-6 mb-3 mb-md-0 featured-image-field">
            <label htmlFor="featured_image" className="form-label">
              Featured Image <span>*</span>
            </label>
            <div className="file-picker-control">
              <button
                type="button"
                className="designBtn file-picker-button"
                onClick={() => featuredImageInputRef.current?.click()}
              >
                Choose File
              </button>
              <span className="file-picker-name">
                {formData.featured_image?.name || "No file chosen"}
              </span>
            </div>
            <input
              ref={featuredImageInputRef}
              type="file"
              className="visually-hidden-file-input"
              id="featured_image"
              name="featured_image"
              accept="image/*"
              onChange={handleFeaturedImageChange}
            />
          </div>
          {selectedCategory?.name !== "Suits" && (
            <div className="col-md-6">
              <label htmlFor="featured_image_color" className="form-label">
                Featured Image Color <span>*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="featured_image_color"
                name="featured_image_color"
                required
                list="product-image-color-options"
                value={formData.featured_image_color?.label || ""}
                onChange={handleFeaturedImageColorChange}
                placeholder="Color Name"
              />
            </div>
          )}
        </div>
        {featuredImagePreview && (
          <div className="mt-2 position-relative images" style={{ width: "100px" }}>
            <img src={featuredImagePreview} alt="Featured preview" />
            <button
              type="button"
              className="btn position-absolute top-0 end-0"
              onClick={removeFeaturedImage}
            >
              ×
            </button>
          </div>
        )}
      </div>

      <div className="mb-5">
        <div className="row align-items-end">
          <div className="col-md-6 mb-3 gallery-images-field">
            <label htmlFor="gallery_images" className="form-label">
              Gallery Images
            </label>
            <div className="file-picker-control">
              <button
                type="button"
                className="designBtn file-picker-button"
                onClick={() => galleryImagesInputRef.current?.click()}
              >
                Choose Files
              </button>
              <span className="file-picker-name">
                {pendingGalleryImages.length > 0
                  ? `${pendingGalleryImages.length} file(s) chosen`
                  : editingGalleryIndex !== null
                    ? "Current image selected"
                  : "No files chosen"}
              </span>
            </div>
            <input
              ref={galleryImagesInputRef}
              type="file"
              className="visually-hidden-file-input"
              id="gallery_images"
              name="gallery_images"
              accept="image/*"
              multiple={editingGalleryIndex === null}
              onChange={handleGalleryImagesChange}
            />
          </div>
          {selectedCategory?.name !== "Suits" && (
            <div className="col-md-4 mb-3">
              <label htmlFor="gallery_image_color" className="form-label">Gallery Image Color</label>
              <input
                type="text"
                className="form-control"
                id="gallery_image_color"
                name="gallery_image_color"
                list="product-image-color-options"
                value={pendingGalleryImageColor?.label || ""}
                onChange={handlePendingGalleryImageColorChange}
                placeholder="Color Name"
              />
            </div>
          )}
          <div className="col-md-2 image-button">
            <button
              type="button"
              className="designBtn"
              onClick={handleAddGalleryImages}
            >
              {editingGalleryIndex !== null ? "Update" : "Add"}
            </button>
          </div>
        </div>
        <datalist id="product-image-color-options">
          {getColorOptions().map((color) => (
            <option key={`${color.label}-${color.code}`} value={color.label} />
          ))}
        </datalist>
        <div className="d-flex flex-wrap gap-2">
          {galleryImagePreviews.length > 0 && (
            <div className="mt-2 d-flex flex-wrap gap-2">
              {galleryImagePreviews.map((preview, index) => (
                <div
                  key={index}
                  className="position-relative images"
                  style={{ width: "100px" }}
                  onClick={() => handleEditGalleryImage(index)}
                >
                  <img src={preview} alt={`Gallery ${index + 1}`} />
                  {selectedCategory?.name !== "Suits" && galleryImagePreviewColors[index] && (
                    <span
                      className="position-absolute bottom-0 start-0 text-white px-1"
                      style={{
                        backgroundColor: "rgba(0,0,0,0.65)",
                        fontSize: "11px",
                        maxWidth: "100%",
                      }}
                    >
                      {galleryImagePreviewColors[index].label}
                    </span>
                  )}
                  <button
                    type="button"
                    className="btn position-absolute top-0 end-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeGalleryImage(index);
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mb-3 form-check">
        <input
          type="checkbox"
          className="form-check-input"
          id="is_featured"
          name="is_featured"
          checked={formData.is_featured}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, is_featured: e.target.checked }))
          }
        />
        <label className="form-check-label" htmlFor="is_featured">
          Mark as Featured Product
        </label>
      </div>

      <button type="submit" className="btn btn-primary" disabled={updating}>
        {updating ? "Saving..." : "Save Changes"}
      </button>
    </form>
  );
};

export default EditPackageForm;
